import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Hangar } from "../../../types";
import mixpanel from "../../../utils/mixpanel";
import { UtilizationDisplay } from "./UtilizationDisplay";

interface HangarRowProps {
  hangar: Hangar;
  aircraft: string;
  aircraftInside: number;
  aircraftTotal: number;
  utilization: string;
  utilizationStacked: number;
  utilizationAll: number;
  estimatedValue?: string;
  estimatedTowTime: string;
  lastStacked: Date | null;
  displayEditTenants: boolean;
  enableScheduling: boolean;
  isTablet: boolean;
  displayEstimatedValue: boolean;
}

export const HangarRow: React.FC<HangarRowProps> = ({
  hangar,
  aircraftInside,
  aircraftTotal,
  utilizationStacked,
  utilizationAll,
  estimatedValue,
  estimatedTowTime,
  lastStacked,
  displayEditTenants,
  enableScheduling,
  isTablet,
  displayEstimatedValue,
}) => {
  const navigate = useNavigate();

  const gridColumns = isTablet
    ? "200px 160px 180px 120px 1fr"
    : displayEstimatedValue
    ? "200px 160px 180px 120px 140px 120px 1fr"
    : "200px 160px 180px 140px 120px 1fr";

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: gridColumns,
        alignItems: "center",
        gap: 2,
        px: 2.5,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "grey.200",
        transition: "background-color 0.15s ease",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      {/* Hangar Name */}
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {hangar.name}
      </Typography>

      {/* Aircraft */}
      <Box sx={{ textAlign: "right" }}>
        <Typography variant="body2">
          <Typography component="span" sx={{ fontWeight: 500 }}>
            {aircraftInside}
          </Typography>
          <Typography component="span" color="text.secondary" sx={{ mx: 0.5 }}>
            of
          </Typography>
          <Typography component="span" sx={{ fontWeight: 500 }}>
            {aircraftTotal}
          </Typography>
          <Typography component="span" color="text.secondary" sx={{ ml: 0.5 }}>
            aircraft
          </Typography>
        </Typography>
      </Box>

      {/* Utilization */}
      <UtilizationDisplay
        stackedUtilization={utilizationStacked}
        allUtilization={utilizationAll}
      />

      {/* Est. Value (desktop only) */}
      {!isTablet && displayEstimatedValue && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "right" }}
        >
          {estimatedValue}
        </Typography>
      )}

      {/* Est. Tow Time (desktop only) */}
      {!isTablet && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "right" }}
        >
          {estimatedTowTime}
        </Typography>
      )}

      {/* Last Stacked */}
      <Tooltip
        title={lastStacked ? lastStacked.toISOString() : "Never stacked"}
        arrow
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontWeight: 400, textAlign: "right" }}
        >
          {lastStacked ? format(lastStacked, "MMM d") : "Never"}
        </Typography>
      </Tooltip>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1}
        justifyContent="flex-end"
        sx={{ flexWrap: "nowrap" }}
      >
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={() => {
            mixpanel.track("Clicked Hangar", {
              location_name: hangar.name,
              location_type: "hangar",
            });
            navigate(`/hangars/${hangar.id}`);
          }}
          sx={{
            whiteSpace: "nowrap",
            minWidth: "fit-content",
            px: 1.5,
            py: 0.4,
            fontSize: "0.75rem",
            borderRadius: "14px",
          }}
        >
          Current Hangar
        </Button>
        {enableScheduling && (
          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={() => {
              mixpanel.track("Clicked Hangar Schedule", {
                location_name: hangar.name,
                location_type: "hangar",
              });
              navigate(`/hangars/${hangar.id}/schedule`);
            }}
            sx={{
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              px: 1.5,
              py: 0.4,
              fontSize: "0.75rem",
              borderRadius: "14px",
            }}
          >
            Hangar Schedule
          </Button>
        )}
        {displayEditTenants && (
          <Button
            size="small"
            variant="outlined"
            sx={{
              color: "text.primary",
              borderColor: "grey.300",
              whiteSpace: "nowrap",
              minWidth: "fit-content",
              px: 1.5,
              py: 0.4,
              fontSize: "0.75rem",
              borderRadius: "14px",
              "&:hover": {
                borderColor: "grey.400",
                bgcolor: "grey.50",
              },
            }}
            onClick={() => {
              navigate(`/reference-hangars/${hangar.id}`);
            }}
          >
            Edit Tenants
          </Button>
        )}
      </Stack>
    </Box>
  );
};
